import { ApiService } from "@core/api/api-service";
import { CountryUserConfig, ProfessionalIdentifier, VerificationConfig } from "@domain/localization/country";



type CountryDto = {
	code: string;
};

type LocaleDto = {
	locale: string;
};

export class CountryService {
	constructor(
		private readonly masterAxiosApiService: ApiService,
		private readonly unauthenticatedApiService: ApiService,
	) {}

	async getLocale(email: string): Promise<string> {
		return (
			await this.unauthenticatedApiService.get<LocaleDto>("/localization/locale", {
				params: { email },
			})
		).data.locale;
	}

	async getDefaultCountry(): Promise<string> {
		return (await this.masterAxiosApiService.get<CountryDto>("/localization/default-country")).data.code;
	}

	async getDefaultLocalesMapping(): Promise<Record<string, string>> {
		return (await this.masterAxiosApiService.get<Record<string, string>>("/localization/default-locales-mapping"))
			.data;
	}

	async getSupportedCountries(): Promise<string[]> {
		return (await this.masterAxiosApiService.get<CountryDto[]>("/localization/supported-countries")).data.map(
			({ code }) => code,
		);
	}

	async getSupportedLocales(): Promise<Map<string, string[]>> {
		const result = (
			await this.masterAxiosApiService.get<{ country: string; locales: string[] }[]>(
				"/localization/supported-locales",
			)
		).data;
		return new Map(result.map(({ country, locales }) => [country, locales]));
	}

	async getCountryUserConfig(countryCode: string): Promise<CountryUserConfig> {
		const result = await this.unauthenticatedApiService.get<CountryUserConfigDto>("/localization/country-config", {
			params: { countryCode },
		});
		return countryConfigFromDto(result.data);
	}

	async getVerificationConfig(countryCode: string): Promise<VerificationConfig> {
		const result = await this.unauthenticatedApiService.get<VerificationConfig>(
			`/config/verification-config/${countryCode}`,
		);
		return result.data;
	}
}

type CountryUserConfigDto = Omit<CountryUserConfig, "ptProfessionalIds"> & {
	ptProfessionalIds: ProfessionalIdentifier[];
};

function countryConfigFromDto(dto: CountryUserConfigDto): CountryUserConfig {
	return {
		...dto,
		ptProfessionalIds: new Map(dto.ptProfessionalIds.map(({ name, ...rest }) => [name, rest])),
	};
}
